"use client";

import { useMemo } from "react";
import { useParams, usePathname, useSearchParams } from "next/navigation";
import { useContactByEmail } from "@/features/contacts/hooks";
import { useContactHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { useModalContext } from "@/lib/hooks/ui/useModalContext";
import { extractExplicitContactId, getStringProperty } from "@/utils/entity-type-guards";
import type { LogActivityModalData } from "@/lib/modals/data/modal-data-types";

/**
 * Resolves the contact context for the Log Activity modal.
 * Reads from modal data, URL params, and falls back to email lookup.
 */
export function useLogActivityContact() {
  const pathname = usePathname();
  const params = useParams<{ id?: string }>();
  const searchParams = useSearchParams();
  const modalData = useModalContext<LogActivityModalData>("log-activity");

  const modalItem = useMemo(() => {
    if (!modalData?.item || typeof modalData.item !== "object") return undefined;
    return modalData.item;
  }, [modalData?.item]);

  // URL/path fallback for direct deep-link opens like /contacts/:id?modal=log-activity
  const routeContactId =
    (pathname?.startsWith("/contacts/") ? params?.id : null) ||
    searchParams?.get("contactId") ||
    searchParams?.get("contactsId");

  const contactId = useMemo(() => {
    if (modalData?.contactId) return modalData.contactId;
    if (!modalItem) return routeContactId || undefined;

    const enriched = getStringProperty(modalItem, "effectiveLogActivityContactId");
    if (enriched) return enriched;

    const entityType = getStringProperty(modalItem, "entity_type")?.toLowerCase();
    const entityId = getStringProperty(modalItem, "entity_id");
    if ((entityType === "contact" || entityType === "contacts") && entityId) return entityId;

    const explicit = extractExplicitContactId(modalItem);
    if (explicit) return explicit;

    return routeContactId || undefined;
  }, [modalData?.contactId, modalItem, routeContactId]);

  const modalContactEmail = useMemo(() => {
    if (modalData?.contactEmail) return modalData.contactEmail;
    if (modalData?.senderEmail) return modalData.senderEmail;
    if (!modalItem) return undefined;
    return getStringProperty(modalItem, "contactEmail") || getStringProperty(modalItem, "senderEmail");
  }, [modalData?.contactEmail, modalData?.senderEmail, modalItem]);

  // Email-based fallback lookup when no contact ID is available
  const { data: contactByEmail } = useContactByEmail(!contactId && modalContactEmail ? modalContactEmail : "");
  const effectiveContactId = contactId || contactByEmail?.id;

  const { data: contact, isLoading: isLoadingContact } = useContactHooks().useSingle(
    effectiveContactId || "",
    { enabled: !!effectiveContactId }
  );

  return { effectiveContactId, contact, isLoadingContact };
}
